import { create } from "zustand";
import { apiFetch } from "@/lib/api";

type ChannelType =
  | "crypto"
  | "ecommerce"
  | "news"
  | "education"
  | "entertainment"
  | "community"
  | "other";

interface ChannelStats {
  totalPosts: number;
  postsThisWeek: number;
  postsThisMonth: number;
}

interface ChannelConfig {
  id: string;
  chatId: string;
  title: string;
  titleAm: string;
  channelType: ChannelType;
  subscriberCount: number;
  isActive: boolean;
  stats: ChannelStats;
  scheduledPosts: number;
}

interface ScheduledPost {
  id: string;
  channelId: string;
  content: string;
  scheduledAt: string;
  status: "pending" | "published" | "failed" | "cancelled";
}

interface ApiChannelConfig {
  id: number;
  chatId: string;
  title: string;
  channelType: ChannelType;
  subscriberCount: number;
  isActive: boolean;
}

function normalizeChannel(channel: ApiChannelConfig): ChannelConfig {
  return {
    id: channel.chatId,
    chatId: channel.chatId,
    title: channel.title,
    titleAm: channel.title,
    channelType: channel.channelType,
    subscriberCount: channel.subscriberCount,
    isActive: channel.isActive,
    stats: { totalPosts: 0, postsThisWeek: 0, postsThisMonth: 0 },
    scheduledPosts: 0,
  };
}

interface ChannelState {
  channels: ChannelConfig[];
  selectedChannel: ChannelConfig | null;
  scheduledPosts: ScheduledPost[];
  isLoading: boolean;
  error: string | null;
}

interface ChannelActions {
  fetchChannels: () => Promise<void>;
  selectChannel: (channelId: string) => void;
  updateChannelType: (channelId: string, type: ChannelType) => Promise<void>;
  addScheduledPost: (
    channelId: string,
    content: string,
    scheduledAt: string,
  ) => Promise<void>;
  cancelScheduledPost: (postId: string) => Promise<void>;
  clearError: () => void;
}

export const useChannelStore = create<ChannelState & ChannelActions>(
  (set, get) => ({
    channels: [],
    selectedChannel: null,
    scheduledPosts: [],
    isLoading: false,
    error: null,

    fetchChannels: async () => {
      set({ isLoading: true, error: null });
      try {
        const response = await apiFetch<{ channels: ApiChannelConfig[] }>(
          "/api/channels",
        );
        const channels = response.channels.map(normalizeChannel);
        set({ channels, isLoading: false });
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Failed to load channels";
        set({ channels: [], isLoading: false, error: message });
      }
    },

    selectChannel: (channelId) => {
      const channel = get().channels.find((c) => c.id === channelId) ?? null;
      set({ selectedChannel: channel });
    },

    updateChannelType: async (channelId, type) => {
      set({ error: null });
      try {
        const updated = await apiFetch<ChannelConfig>(
          `/api/channels/${channelId}`,
          { method: "PUT", body: { channelType: type } },
        );
        set({
          channels: get().channels.map((ch) =>
            ch.id === channelId ? updated : ch,
          ),
          selectedChannel:
            get().selectedChannel?.id === channelId
              ? updated
              : get().selectedChannel,
        });
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Failed to update channel type";
        set({ error: message });
      }
    },

    addScheduledPost: async (channelId, content, scheduledAt) => {
      set({ error: null });
      try {
        const newPost = await apiFetch<ScheduledPost>(
          `/api/channels/${channelId}/posts`,
          { method: "POST", body: { content, scheduledAt } },
        );
        set({ scheduledPosts: [...get().scheduledPosts, newPost] });
        await get().fetchChannels();
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Failed to schedule post";
        set({ error: message });
      }
    },

    cancelScheduledPost: async (postId) => {
      set({ error: null });
      try {
        await apiFetch<{ ok: true }>(
          `/api/channels/posts/${postId}/cancel`,
          { method: "PUT" },
        );
        set({
          scheduledPosts: get().scheduledPosts.map((post) =>
            post.id === postId ? { ...post, status: "cancelled" } : post,
          ),
        });
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Failed to cancel scheduled post";
        set({ error: message });
      }
    },

    clearError: () => set({ error: null }),
  }),
);

export type { ChannelConfig, ChannelType, ChannelStats, ScheduledPost };
